import React from 'react';

import { Helmet } from 'react-helmet';
import { Navigate, useParams } from 'react-router-dom';

import { Box, Container } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import { preRegisterSpotlightRoutes } from '~/components/Common/Spotlight/SpotlightProvider';
import LowInventoryNotifications from '~/components/PriceParrot/LowInventoryNotifications';
import ParityNotifications from '~/components/PriceParrot/ParityNotifications';
import PriceParrotChainParityDashboard from '~/components/PriceParrot/PriceParrotChainParityDashboard';
import PriceParrotControlCentre from '~/components/PriceParrot/PriceParrotControlCentre';
import PriceParrotParityTool from '~/components/PriceParrot/PriceParrotParityTool';
import PriceParrotScheduler from '~/components/PriceParrot/PriceParrotScheduler';
import PriceParrotShopper from '~/components/PriceParrot/PriceParrotShopper';
import RateDoctor from '~/components/RateDoctor/RateDoctor';
import ChainsListPage from '~/components/Vendors/Chains/ChainsListPage';

import AriAnomaliesPage from './AriAnomalies/AriAnomaliesPage';
import AriSearchPage from './AriSearch/AriSearchPage';
import { isAriSearchEnabled } from './AriSearch/constants';
import { createComingSoon } from './ComingSoon';
import ConnectivityEmailsPage from './ConnectivityEmails/ConnectivityEmailsPage';
import ExtranetApprovalsPage from './ExtranetApprovals/ExtranetApprovalsPage';
import LPCVendorPerformancePage from './LPCVendorPerformance/LPCVendorPerformancePage';
import NavTabs from './NavTabs';

const BASE_PATH = '/commercial-ops';
const DEFAULT_SECTION = 'price-parity';

interface TabEntry {
  label: string;
  slug: string;
  component: React.FC;
  keywords?: string;
}

interface SectionWithSubsections {
  label: string;
  slug: string;
  subsections: ReadonlyArray<TabEntry>;
  defaultSubsection: string;
}

interface FlatSection {
  label: string;
  slug: string;
  component: React.FC;
  keywords?: string;
}

/**
 * A nav entry that lives in the Commercial Ops tab strip for discoverability
 * but routes to a page outside `/commercial-ops` — used when the target page
 * has its own chrome (Container + PageHeader) or canonical URL we don't want
 * to break (e.g. BriefCase share links at `/briefcase/:jobId`).
 */
interface LinkSection {
  label: string;
  slug: string;
  href: string;
  keywords?: string;
}

type Section = SectionWithSubsections | FlatSection | LinkSection;

function hasSubsections(section: Section): section is SectionWithSubsections {
  return 'subsections' in section;
}

function isLinkSection(section: Section): section is LinkSection {
  return 'href' in section;
}

const PRICE_PARITY_SUBSECTIONS: ReadonlyArray<TabEntry> = [
  { label: 'Shopper', slug: 'shopper', component: PriceParrotShopper },
  { label: 'Scheduler', slug: 'scheduler', component: PriceParrotScheduler },
  { label: 'Parity Tool', slug: 'parity-tool', component: PriceParrotParityTool, keywords: 'parity report' },
  {
    label: 'Chain Parity',
    slug: 'chain-parity',
    component: PriceParrotChainParityDashboard,
    keywords: 'chain rate parity dashboard hotel group',
  },
  { label: 'Control Centre', slug: 'control-centre', component: PriceParrotControlCentre, keywords: 'PPCC' },
];

const VENDOR_NOTIFICATIONS_SUBSECTIONS: ReadonlyArray<TabEntry> = [
  { label: 'Parity Notifications', slug: 'parity-notifications', component: ParityNotifications },
  {
    label: 'Low Inventory Notifications',
    slug: 'low-inventory-notifications',
    component: LowInventoryNotifications,
  },
];

const CONTENT_MANAGEMENT_SUBSECTIONS: ReadonlyArray<TabEntry> = [
  { label: 'Images', slug: 'images', component: createComingSoon('Images') },
  { label: 'Room Content', slug: 'room-content', component: createComingSoon('Room Content') },
];

const ALL_SECTIONS: ReadonlyArray<Section> = [
  {
    label: 'Price Parity',
    slug: 'price-parity',
    subsections: PRICE_PARITY_SUBSECTIONS,
    defaultSubsection: 'shopper',
  },
  {
    label: 'Extranet',
    slug: 'extranet',
    component: ExtranetApprovalsPage,
  },
  {
    label: 'Vendor Notifications',
    slug: 'vendor-notifications',
    subsections: VENDOR_NOTIFICATIONS_SUBSECTIONS,
    defaultSubsection: 'parity-notifications',
  },
  {
    label: 'LPC Vendor Performance',
    slug: 'lpc-vendor-performance',
    component: LPCVendorPerformancePage,
    keywords: 'email generator lpc tier 1 vendor monthly quarterly',
  },
  {
    label: 'Content Management',
    slug: 'content-management',
    subsections: CONTENT_MANAGEMENT_SUBSECTIONS,
    defaultSubsection: 'images',
  },
  {
    label: 'Rate Doctor',
    slug: 'rate-doctor',
    component: RateDoctor,
    keywords: 'detect missing rates products derbysoft synxis',
  },
  {
    label: 'ARI Search',
    slug: 'ari-search',
    component: AriSearchPage,
    keywords:
      'ari logs availability rates inventory room rate push history channel manager derbysoft synxis siteminder',
  },
  {
    label: 'ARI Anomalies',
    slug: 'ari-anomalies',
    component: AriAnomaliesPage,
    keywords: 'ari anomaly connectivity rate inventory margin derbysoft synxis siteminder',
  },
  {
    label: 'Connectivity Emails',
    slug: 'connectivity-emails',
    component: ConnectivityEmailsPage,
    keywords: 'connectivity email automation salesforce case classification',
  },
  {
    label: 'BriefCase',
    slug: 'briefcase',
    href: '/briefcase',
    keywords: 'ai post campaign deck generator powerpoint pcm report',
  },
  {
    label: 'Chains',
    slug: 'chains',
    component: () => <ChainsListPage basePath={`${BASE_PATH}/chains`} embedded />,
    keywords: 'chain rate rules commercial rate plans vendors',
  },
];

const SECTIONS: ReadonlyArray<Section> = ALL_SECTIONS.filter(
  (section) => section.slug !== 'ari-search' || isAriSearchEnabled(),
);

// Spotlight: register one entry per leaf (flat section or nested subsection).
// Module-level side effect — requires a static import in App.tsx (not lazy-loaded)
// so that entries are registered before SpotlightProvider renders.
preRegisterSpotlightRoutes(
  SECTIONS.flatMap((section) => {
    if (hasSubsections(section)) {
      return section.subsections.map((sub) => ({
        path: `${BASE_PATH}/${section.slug}/${sub.slug}`,
        label: `Commercial Ops / ${section.label} / ${sub.label}`,
        section: 'Commercial Ops',
        keywords: sub.keywords,
      }));
    }
    return [
      {
        // LinkSection points outside `/commercial-ops`; register the external
        // href so Spotlight selection navigates straight to the real page.
        path: isLinkSection(section) ? section.href : `${BASE_PATH}/${section.slug}`,
        label: `Commercial Ops / ${section.label}`,
        section: 'Commercial Ops',
        keywords: section.keywords,
      },
    ];
  }),
);

export default function CommercialOpsPage() {
  const { section, subsection } = useParams<{ section?: string; subsection?: string }>();
  if (!section) {
    return <Navigate to={`${BASE_PATH}/${DEFAULT_SECTION}`} replace />;
  }

  const currentSection = SECTIONS.find((s) => s.slug === section);
  if (!currentSection) {
    return <Navigate to={`${BASE_PATH}/${DEFAULT_SECTION}`} replace />;
  }

  // Direct URL hits on a link-only section (e.g. /commercial-ops/briefcase) bounce
  // straight to the canonical page so the user lands on a single source of truth.
  if (isLinkSection(currentSection)) {
    return <Navigate to={currentSection.href} replace />;
  }

  let LeafComponent: React.FC;
  let subNav: React.ReactNode = null;

  if (hasSubsections(currentSection)) {
    const activeSub = subsection ? currentSection.subsections.find((s) => s.slug === subsection) : undefined;
    if (!activeSub) {
      return <Navigate to={`${BASE_PATH}/${section}/${currentSection.defaultSubsection}`} replace />;
    }
    LeafComponent = activeSub.component;
    subNav = (
      <NavTabs
        value={activeSub.slug}
        tabs={currentSection.subsections}
        buildHref={(slug) => `${BASE_PATH}/${section}/${slug}`}
      />
    );
  } else {
    LeafComponent = currentSection.component;
  }

  return (
    <Container maxWidth="xl" sx={{ bgcolor: 'background.default', color: 'text.primary' }}>
      <Helmet>
        <title>{`Commercial Ops / ${currentSection.label}`}</title>
      </Helmet>

      <PageHeader title="Commercial Ops" />

      <NavTabs value={currentSection.slug} tabs={SECTIONS} buildHref={(slug) => `${BASE_PATH}/${slug}`} />

      {subNav}

      <Box mt={4}>
        <LeafComponent />
      </Box>
    </Container>
  );
}
